import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { LocaleService } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { SiteActions, SiteState } from '@next/core-lib/site';
import { UserSelectors } from '@next/core-lib/user';
import { WindowService, WindowServiceInjector } from '@next/core-lib/window';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { CropFieldAdvice, CropFieldImpact } from 'src/app/shared/models';
import { GetEnvironmentalImpactReport } from 'src/app/store/actions';
import {
  getCropYear,
  getReportSort,
  selectCropFieldAdvices,
  selectEnvironmentalImpactTableReport,
  selectLoggedInEmployeeAsArray,
} from 'src/app/store/selectors';

import { ReportSortService } from '../services/report-sort.service';

@Component({
  selector: 'app-environmental-impact-report',
  templateUrl: './environmental-impact-report.html',
  styleUrls: ['./../reports.scss'],
  standalone: false,
})
export class EnvironmentalImpactReportComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly sortService = inject(ReportSortService);
  protected readonly router = inject(Router);
  private readonly window = inject<WindowService>(WindowServiceInjector);

  destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  cropYear$ = this.store.pipe(select(getCropYear));
  customer$ = this.store.pipe(select(UserSelectors.selectCustomer));
  pageTitle$ = combineLatest([this.cropYear$, this.customer$]).pipe(
    map(
      ([cropYear, customer]) =>
        `${customer?.name ? customer.name : ''} - ${this.locale.translate(
          'environmentalImpact',
        )} ${cropYear}`,
    ),
  );
  relatedPeople$ = this.store.pipe(select(selectLoggedInEmployeeAsArray));
  cropFieldIds$ = this.store.pipe(select(RouterSelectors.getRouteParam('cropFieldIds')));
  tableData$: Observable<CropFieldImpact[]> = new Observable();
  sortedTableData$: Observable<CropFieldImpact[]> = new Observable();
  cropFieldAdvices$ = this.store.pipe(select(selectCropFieldAdvices));

  cropYear: number;
  cropFieldIds: number[];

  reportSort$ = this.store.pipe(select(getReportSort));

  buttonItems$ = this.sortService.getButtonItems();

  ngOnInit() {
    this.pageTitle$.pipe(takeUntil(this.destroyed$)).subscribe((pageTitle) => {
      this.store.dispatch(SiteActions.updatePageTitle({ pageTitle }));
    });

    this.cropYear$
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((cropYear) => {
        this.cropYear = cropYear;

        this.getReport();
      });

    this.cropFieldIds$
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((fieldIdsString) => {
        this.cropFieldIds = JSON.parse(fieldIdsString) as number[];

        this.getReport();
      });

    this.reportSort$.subscribe(
      (reportSort) =>
        (this.sortedTableData$ = this.tableData$.pipe(
          map((tableData) =>
            this.sortService.sort({
              elements: tableData,
              option: reportSort.option,
              descending: reportSort.descending,
              cropFieldNameFunc: (item) => item.cropFieldName,
              cropNameFunc: (item) => item.cropName + ' ' + item.cropVarietyName,
            }),
          ),
        )),
    );
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  print() {
    if (this.window) {
      this.window.print();
    }
  }

  back() {
    this.router.navigate(['/']);
  }

  getAdviceIds(fieldIds: number[], cropFieldAdvices: CropFieldAdvice[]) {
    const adviceIds: number[] = [];
    fieldIds.forEach((fieldId) => {
      const filteredCropFieldAdvices = cropFieldAdvices.filter((a) => a.cropFieldId === fieldId);
      const adviceId = filteredCropFieldAdvices.length
        ? filteredCropFieldAdvices[0].adviceId
        : null;
      if (adviceId) {
        adviceIds.push(adviceId);
      }
    });
    return adviceIds;
  }

  buttonAction(data: string) {
    this.sortService.handleButtonAction(data);
  }

  private getReport = () => {
    if (this.cropYear && this.cropFieldIds?.length > 0) {
      this.store.dispatch(new GetEnvironmentalImpactReport(this.cropFieldIds));

      this.tableData$ = this.store.pipe(
        select(selectEnvironmentalImpactTableReport),
        takeUntil(this.destroyed$),
      );
    }
  };
}
